import { useCallback } from 'react';

import Sentry from '@lib/analytics/sentry';
import { getStoredSession } from '@lib/api/client';
import useToast from '@lib/hooks/useToast';
import { type ApplicationState, useApplicationState } from '@lib/state';
import { type MfaTriggeredBy } from '@lib/state/Mfa';

/**
 * Mid-session account-verification entry for the "verify your account" prompt shown beside
 * the promo-code field at checkout. Distinct from the login/signup 428 gate: the user is
 * already logged in, so there is no `pendingMfa` and no fresh 428 bearer.
 *
 * `startVerification` synthesizes a `pendingMfa` with `origin: 'checkout'` carrying the LIVE
 * session token as `authToken`, so the existing PhoneNumberScreen (mobile) / PhoneNumberModal
 * (web) AV branch works unchanged — those screens key off `pendingMfa.authToken` and
 * `requiresAccVerification`, and their `finishMfaResolution` else-branch already refreshes
 * profile-info on success (flipping the persisted `isPhoneValidated`).
 *
 * The token is read from STORAGE, not `state.auth`. The 401 refresh interceptor writes the
 * rotated bearer only to storage (`setStoredSession`), so after any silent refresh — e.g. the
 * profile fetch on every cold start of a session older than the 2h JWT lifetime — `state.auth`
 * still holds the dead token. The `phone/*` routes are on the `BearerTokenBlacklist`, so a
 * dead bearer there gets no refresh-and-retry: `phone/start` 401s ("expired" toast) and
 * `phone/skip` 401s → the collectors log the user out of a perfectly live session. Same
 * mechanism and fix as `resolveSessionToken` in MfaChallengeModal.
 *
 * Routing to the entry surface differs per platform, so the caller passes a `navigate`
 * callback: mobile does `navigation.navigate('PhoneNumber')`; web does
 * `useModal('phoneNumber').onOpen()`. The prompt itself is gated at the call site by
 * `mfa-account-verification`, so this hook assumes it's only invoked when the flag is on.
 *
 * `triggeredBy` names the surface (`checkout` nudge, `rewards` gate, `profile-edit` banner)
 * for the `triggeredBy` telemetry prop (JSAD-6706); the collector reads it off `pendingMfa`.
 */
const useCheckoutAccountVerification = () => {
    const { toast } = useToast();
    const setPendingMfa = useApplicationState((s: ApplicationState) => s.auth.setPendingMfa);

    const startVerification = useCallback(
        async (
            navigate: () => void,
            triggeredBy: Extract<MfaTriggeredBy, 'checkout' | 'rewards' | 'profile-edit'>
        ) => {
            const sessionToken = (await getStoredSession())?.authToken?.token;
            if (!sessionToken) {
                // The surfaces that call this only render for a signed-in user, so a missing
                // token is a defect, not a user state — record it rather than toast silently.
                Sentry.captureMessage('startVerification: no stored session', {
                    level: 'warning',
                    extra: { triggeredBy }
                });
                toast({
                    level: 'error',
                    message: 'Please sign in again to verify your account.'
                });
                return;
            }

            setPendingMfa({
                requiresAccVerification: true,
                requiresDeviceVerification: false,
                authToken: sessionToken,
                origin: 'checkout',
                triggeredBy
            });
            navigate();
        },
        [setPendingMfa, toast]
    );

    return { startVerification };
};

export default useCheckoutAccountVerification;
